import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import isEqual from 'lodash.isequal';
import { useAppSelector, useAppDispatch } from '@store';
import {
  ajvResolver,
  getResponseErrors,
  ResponseError,
  responseErrorsResolver,
  ValidatorsProps,
} from '@ui/src';
import {
  useEditPSPDetailsMutation,
  useLazyGetPSPDetailsQuery,
} from '../../../settings/store/services/details';
import {
  EditPSPDetailsFormValues,
  PSPDetailsKeys,
} from '../../../settings/types';
import { setIsDirty } from '../../../settings/store/slices/details';

const useDetails = () => {
  const {
    settings: {
      details: { validators, pspDetails },
    },
    administrationDictionaries: { countries },
  } = useAppSelector((state) => state);

  const dispatch = useAppDispatch();

  const [edit, { isLoading: editIsLoading }] = useEditPSPDetailsMutation();
  const [getDetails, { isLoading }] = useLazyGetPSPDetailsQuery();
  const [defaultFormValues, setDefaultFormValues] =
    useState<EditPSPDetailsFormValues>();
  const [globalError, setGlobalError] = useState('');
  const [errorList, setErrorList] = useState('');

  const {
    control,
    handleSubmit,
    formState: { errors },
    setError,
    reset,
    watch,
  } = useForm<EditPSPDetailsFormValues>({
    resolver: ajvResolver(validators?.updateSettingSchema as ValidatorsProps),
  });

  const fields = watch();

  const isDirty =
    !isEqual(fields, defaultFormValues) || Boolean(Object.keys(errors).length);

  const getDetailsAction = useCallback(async () => {
    const response = (await getDetails()) as { error?: ResponseError };

    if (response?.error) {
      getResponseErrors({
        error: response?.error,
        setErrorList,
      });
    }
  }, []);

  const onSubmit = handleSubmit(async (values) => {
    setGlobalError('');

    const updatedSettings: {
      [key: string]:
        | string
        | boolean
        | number
        | Record<string, string>
        | Record<string, boolean>;
      key: PSPDetailsKeys;
    }[] = [];

    if (values.bic !== defaultFormValues?.bic) {
      updatedSettings.push({
        bic: values.bic,
        key: PSPDetailsKeys.BIC,
      });
    }

    if (values.sortcode !== defaultFormValues?.sortcode) {
      updatedSettings.push({
        sortcode: values.sortcode,
        key: PSPDetailsKeys.SORTCODE,
      });
    }

    if (values.finyearstart !== defaultFormValues?.finyearstart) {
      updatedSettings.push({
        finyearstart: values.finyearstart,
        key: PSPDetailsKeys.FIN_YEAR_START,
      });
    }

    if (values.vatPercent !== defaultFormValues?.vatPercent) {
      updatedSettings.push({
        vatPercent: Number(values.vatPercent),
        key: PSPDetailsKeys.VAT_PERCENT,
      });
    }

    if (values.countryId !== defaultFormValues?.countryId) {
      updatedSettings.push({
        countryId: values.countryId,
        key: PSPDetailsKeys.COUNTRY,
      });
    }

    if (values.address !== defaultFormValues?.address) {
      updatedSettings.push({
        address: values.address,
        key: PSPDetailsKeys.ADDRESS,
      });
    }

    if (values.website !== defaultFormValues?.website) {
      updatedSettings.push({
        website: values.website,
        key: PSPDetailsKeys.WEBSITE,
      });
    }

    if (values.email !== defaultFormValues?.email) {
      updatedSettings.push({
        email: values.email,
        key: PSPDetailsKeys.EMAIL,
      });
    }

    if (values.phone !== defaultFormValues?.phone) {
      updatedSettings.push({
        phone: values.phone.includes('+') ? values.phone : `+${values.phone}`,
        key: PSPDetailsKeys.PHONE,
      });
    }

    if (values.license !== defaultFormValues?.license) {
      updatedSettings.push({
        license: values.license,
        key: PSPDetailsKeys.LICENSE,
      });
    }

    if (values.regulator !== defaultFormValues?.regulator) {
      updatedSettings.push({
        regulator: values.regulator,
        key: PSPDetailsKeys.REGULATOR,
      });
    }

    if (
      values.customersDemoModeAvailable !==
      defaultFormValues?.customersDemoModeAvailable
    ) {
      updatedSettings.push({
        customersDemoModeAvailable:
          values.customersDemoModeAvailable as boolean,
        key: PSPDetailsKeys.CUSTOMERS_DEMO_MODE_AVAILABLE,
      });
    }

    if (values.legalName !== defaultFormValues?.legalName) {
      updatedSettings.push({
        legalName: values.legalName,
        key: PSPDetailsKeys.LEGAL_NAME,
      });
    }

    if (updatedSettings.length > 0) {
      const response = (await Promise.all(
        updatedSettings.map(async (item) => {
          const res = await edit(item);

          return res;
        }),
      )) as { error?: ResponseError }[];

      if (response.find((item) => item.error)) {
        const errorsResponse = response.filter((item) => item.error);

        errorsResponse.forEach((item) => {
          responseErrorsResolver({
            errors: item.error as ResponseError,
            setError,
            setGlobalError,
          });
        });
      } else {
        dispatch(setIsDirty(false));
      }

      setDefaultFormValues({
        countryId: values?.countryId || '',
        bic: values?.bic || '',
        sortcode: values?.sortcode || '',
        finyearstart: values?.finyearstart || '',
        legalName: values?.legalName || '',
        address: values?.address || '',
        website: values?.website || '',
        phone: values?.phone || '',
        email: values?.email || '',
        license: values?.license || '',
        regulator: values?.regulator || '',
        customersDemoModeAvailable: values?.customersDemoModeAvailable || false,
        vatPercent: values?.vatPercent || '',
      });
    }
  });

  useEffect(() => {
    if (!pspDetails) {
      getDetailsAction();
    }
  }, [pspDetails]);

  useEffect(() => {
    if (pspDetails) {
      const bic = pspDetails.find((item) => item.key === 'COMPANY-BIC');
      const sortcode = pspDetails.find(
        (item) => item.key === 'COMPANY-SORTCODE',
      );
      const finyearstart = pspDetails.find(
        (item) => item.key === 'COMPANY-FIN-YEAR-START',
      );
      const vatPercent = pspDetails?.find((item) => item.key === 'VAT-PERCENT');
      const countryId = pspDetails.find(
        (item) => item.key === 'COMPANY-COUNTRY',
      );
      const address = pspDetails.find((item) => item.key === 'COMPANY-ADDRESS');
      const website = pspDetails.find((item) => item.key === 'COMPANY-WEBSITE');
      const email = pspDetails.find((item) => item.key === 'COMPANY-EMAIL');
      const phone = pspDetails.find((item) => item.key === 'COMPANY-PHONE');
      const license = pspDetails.find((item) => item.key === 'COMPANY-LICENSE');
      const regulator = pspDetails.find(
        (item) => item.key === 'COMPANY-REGULATOR',
      );
      const customersDemoModeAvailable = pspDetails.find(
        (item) => item.key === 'CUSTOMERS-DEMO-MODE-AVAILABLE',
      );
      const legalName = pspDetails.find(
        (item) => item.key === 'COMPANY-LEGAL-NAME',
      );

      reset({
        bic: (bic?.data.value as string) || '',
        sortcode: (sortcode?.data.value as string) || '',
        finyearstart: (finyearstart?.data.value as string) || '',
        vatPercent: (vatPercent?.data.value as string) || '',
        countryId: (countryId?.data.value as string) || '',
        address: (address?.data.value as string) || '',
        website: (website?.data.value as string) || '',
        email: (email?.data.value as string) || '',
        phone: (phone?.data.value as string) || '',
        license: (license?.data.value as string) || '',
        regulator: (regulator?.data.value as string) || '',
        customersDemoModeAvailable:
          (customersDemoModeAvailable?.data?.value as boolean) || false,
        legalName: (legalName?.data.value as string) || '',
      });

      setDefaultFormValues({
        bic: (bic?.data.value as string) || '',
        sortcode: (sortcode?.data.value as string) || '',
        finyearstart: (finyearstart?.data.value as string) || '',
        vatPercent: (vatPercent?.data.value as string) || '',
        countryId: (countryId?.data.value as string) || '',
        address: (address?.data.value as string) || '',
        website: (website?.data.value as string) || '',
        email: (email?.data.value as string) || '',
        phone: (phone?.data.value as string) || '',
        license: (license?.data.value as string) || '',
        regulator: (regulator?.data.value as string) || '',
        customersDemoModeAvailable:
          (customersDemoModeAvailable?.data?.value as boolean) || false,
        legalName: (legalName?.data.value as string) || '',
      });
    }
  }, [pspDetails]);

  useEffect(() => {
    dispatch(setIsDirty(isDirty));
  }, [isDirty]);

  return {
    isLoading,
    errorList,
    control,
    errors,
    onSubmit,
    countries,
    fields,
    globalError,
    editIsLoading,
    isDirty,
  };
};

export default useDetails;
